import { memo, useEffect, useState } from 'react';
import { Activity, Bolt, Gauge, Pin, Power, PowerOff, Save, WalletCards } from 'lucide-react';
import type { Channel } from '../api';
import { PRIVACY_MASK, maskChannelName } from '../privacy';
import { MetricBlock } from './MetricBlock';
import { ProviderMark } from './ProviderMark';
import { Timeline } from './Timeline';

export interface ChannelConfigDraft {
  costCnyPerUsd: number;
  manualPriority: number;
  probeModel: string;
  probePrompt: string;
  probeAsClaudeCode: boolean;
}

interface ChannelCardProps {
  channel: Channel;
  statisticsWindow?: 'today' | '24h';
  viewMode?: 'large' | 'medium' | 'compact';
  refreshLabel: string;
  onOpen: (channel: Channel) => void;
  onSaveConfig: (channel: Channel, config: ChannelConfigDraft) => void;
  onForce: (channel: Channel) => void;
  onSchedulingChange?: (channel: Channel, enabled: boolean) => Promise<void>;
  privacyMode?: boolean;
  balance?: ChannelCardBalance;
}

export interface ChannelCardBalance {
  name: string;
  convertedBalance: number;
  unit: string;
  lowBalance: boolean;
}

function ChannelCardComponent({ channel, statisticsWindow = 'today', viewMode = 'large', refreshLabel, onOpen, onSaveConfig, onForce, onSchedulingChange = async () => {}, privacyMode = false, balance }: ChannelCardProps) {
  const [schedulingPending, setSchedulingPending] = useState(false);
  const usageTimeline = Array.isArray(channel.usageTimeline) ? channel.usageTimeline : [];
  const probeTimeline = Array.isArray(channel.probeTimeline) ? channel.probeTimeline : [];
  const probeLogs = Array.isArray(channel.probeLogs) ? channel.probeLogs : [];
  const isLarge = viewMode === 'large';
  const isMedium = viewMode === 'medium';
  const isCompact = viewMode === 'compact';
  const hasMonitoringData =
    channel.request24h > 0 ||
    channel.officialUsd24h > 0 ||
    probeLogs.length > 0 ||
    usageTimeline.length > 0 ||
    probeTimeline.length > 0;
  const formatLatency = (value: number | null) => ({
    value: value === null ? '未采集' : String(value),
    unit: value === null ? undefined : 'ms',
  });
  const ttft = formatLatency(channel.ttftMs);
  const ping = formatLatency(channel.pingMs);
  const isImageProbe = channel.probeModel.trim().toLowerCase().startsWith('gpt-image-');
  const badgeGrade = channel.enabled ? (channel.dispatchHealthGrade ?? channel.status) : 'unhealthy';
  const badgeTitle = Array.isArray(channel.dispatchHealthReasons) ? channel.dispatchHealthReasons.join('；') : undefined;
  const badgeText = channel.enabled ? (channel.dispatchHealthText || channel.statusText) : '已停用';
  const displayName = privacyMode ? maskChannelName(channel.name) : channel.name;
  const requestCount = statisticsWindow === '24h' ? channel.request24h : (channel.requestToday ?? channel.request24h);
  const officialUsd = statisticsWindow === '24h' ? channel.officialUsd24h : (channel.officialUsdToday ?? channel.officialUsd24h);
  const requestText = privacyMode ? PRIVACY_MASK : requestCount.toLocaleString();
  const officialUsdText = privacyMode ? PRIVACY_MASK : `$${officialUsd.toFixed(2)}`;
  const dispatchScoreText = formatScore(channel.dispatchScore?.total ?? channel.score);
  const costText = privacyMode ? PRIVACY_MASK : `${channel.costCnyPerUsd.toFixed(4)}`;
  const recentWindow = channel.healthWindows?.find((window) => window.window === '30m');
  const hasRecentAvailability = (recentWindow?.requests ?? 0) > 0;
  const availabilityValue = hasRecentAvailability && recentWindow ? recentWindow.successRate : channel.availability7d;
  const availabilityText = hasMonitoringData ? `${availabilityValue.toFixed(1)}%` : '-';
  const compactTtftText = formatCompactLatency(channel.ttftMs);
  const primaryReason = channel.dispatchHealthReasons?.[0] || badgeText || '暂无调度原因';
  const cardClassName = [
    'channel-card',
    `channel-card-${viewMode}`,
    `card-${badgeGrade}`,
    channel.isDispatching ? 'is-dispatching' : '',
    channel.enabled ? '' : 'card-disabled',
  ].filter(Boolean).join(' ');
  const handleSchedulingChange = async (enabled: boolean) => {
    setSchedulingPending(true);
    try {
      await onSchedulingChange(channel, enabled);
    } finally {
      setSchedulingPending(false);
    }
  };

  if (!channel.enabled) {
    return (
      <article className={`${cardClassName} disabled-channel-summary`} role="listitem" aria-label={`${displayName} 已停用账号卡片`}>
        <header className="card-header">
          <ProviderMark provider={channel.provider} label={channel.providerLabel} />
          <div className="card-title">
            <h2><button className="card-title-button" type="button" onClick={() => onOpen(channel)}>{displayName}</button></h2>
            <div className="tags"><span className="tag group-tag" title={channel.group.name}>{channel.group.name}</span></div>
          </div>
          <span className="status-badge status-unhealthy">已停用</span>
        </header>
        <div className="disabled-channel-row">
          <span>成本价 <strong>{privacyMode ? PRIVACY_MASK : `¥${channel.costCnyPerUsd.toFixed(4)}`}</strong></span>
          <span>状态 <strong>{channel.accountStatus || '已停用'}</strong></span>
          <button className="enable-scheduling-button" type="button" disabled={schedulingPending} onClick={() => void handleSchedulingChange(true)}>
            <Power size={15} /> {schedulingPending ? '正在开启' : '开启调度'}
          </button>
        </div>
      </article>
    );
  }

  return (
    <article
      className={cardClassName}
      role="listitem"
      aria-label={`${displayName}${channel.isDispatching ? ' 正在调度' : ''} 账号卡片`}
    >
      <header className="card-header">
        <ProviderMark provider={channel.provider} label={channel.providerLabel} />
        <div className="card-title">
          <h2>
            <button
              className="card-title-button"
              type="button"
              aria-label={`${displayName} 详情`}
              title="查看账号详情"
              onClick={() => onOpen(channel)}
            >
              {displayName}
            </button>
          </h2>
          <div className="tags" aria-label="provider model group tags">
            <span className="tag group-tag" title={channel.group.name}>{channel.group.name}</span>
            {channel.isDispatching && <span className="tag dispatching-tag" title="当前分组最近一次由 SubPilot 选中的账号">调度中</span>}
          </div>
        </div>
        <span className={`status-badge status-${badgeGrade}`} title={badgeTitle}>{badgeText}</span>
      </header>

      {!isCompact && (
        <div className="metric-grid">
          <MetricBlock icon={Bolt} label="TTFT" value={ttft.value} unit={ttft.unit} />
          <MetricBlock icon={Activity} label={isImageProbe ? '生成耗时' : 'Ping'} value={ping.value} unit={ping.unit} />
        </div>
      )}

      {isCompact && (
        <div className="compact-dispatch" aria-label="账号调度摘要">
          <div className="compact-dispatch-head">
            <span className={`mini-status status-${badgeGrade}`}>{badgeText}</span>
            <span className="compact-score"><em>调度分</em><strong>{dispatchScoreText}</strong></span>
          </div>
          <div className="compact-metrics compact-metrics-grid">
            <CompactStat label="成本" value={costText} />
            <CompactStat label="TTFT" value={compactTtftText} />
            <CompactStat label="可用性" value={availabilityText} />
            <CompactStat label="请求" value={requestText} />
          </div>
          <p className="compact-reason" title={primaryReason}>{primaryReason}</p>
          <div className="compact-timelines compact-card-timelines" aria-label="探测和真实请求趋势">
            <CompactTimelineStrip label="探测" points={probeTimeline} />
            <CompactTimelineStrip label="真实" points={usageTimeline} />
          </div>
        </div>
      )}

      {isLarge && (
        <section className="monitor-panel">
          <div className="availability">
            <span>{hasRecentAvailability ? '真实请求成功率 · 30m' : '历史真实请求成功率 · 24h'}</span>
            <strong className={hasMonitoringData ? undefined : 'empty-value'}>{hasMonitoringData ? `${availabilityValue.toFixed(2)}%` : '暂无数据'}</strong>
          </div>

          <div className="dual-timeline">
            <Timeline
              label="自动探测 · 近 60 次"
              points={probeTimeline}
              refreshLabel={refreshLabel}
              sourceLabel="自动探测"
              empty={!hasMonitoringData || probeTimeline.length === 0}
              loading={channel.timelinesLoading}
              error={channel.timelinesError}
            />
            <Timeline
              label="真实请求 · 近 60 次"
              points={usageTimeline}
              refreshLabel={refreshLabel}
              sourceLabel="真实请求"
              empty={!hasMonitoringData || usageTimeline.length === 0}
              loading={channel.timelinesLoading}
              error={channel.timelinesError}
            />
          </div>
        </section>
      )}

      {isMedium && (
        <DispatchFocusPanel
          score={dispatchScoreText}
          cost={costText}
          availability={availabilityText}
          reason={primaryReason}
          probeTimeline={probeTimeline}
          usageTimeline={usageTimeline}
        />
      )}

      {balance && (
        <div className={balance.lowBalance ? 'channel-balance-strip low' : 'channel-balance-strip'} aria-label="渠道余额">
          <span><WalletCards size={14} />渠道余额</span>
          <strong>{privacyMode ? PRIVACY_MASK : formatBalance(balance.convertedBalance, balance.unit)}</strong>
          <small>{privacyMode ? PRIVACY_MASK : balance.name}</small>
        </div>
      )}

      {isLarge && (
        <ChannelCardControls
          channel={channel}
          displayName={displayName}
          privacyMode={privacyMode}
          onForce={onForce}
          onSaveConfig={onSaveConfig}
        />
      )}

      <button
        className="disable-scheduling-button"
        type="button"
        disabled={schedulingPending}
        onClick={() => void handleSchedulingChange(false)}
      >
        <PowerOff size={15} /> {schedulingPending ? '正在关闭' : '关闭调度'}
      </button>

      <footer className="card-foot">
        <span><Gauge size={14} /> {statisticsWindow === '24h' ? '近24小时' : '今日'}请求 {requestText}</span>
        <span>官方 {officialUsdText}</span>
      </footer>
    </article>
  );
}

export const ChannelCard = memo(ChannelCardComponent, (previous, next) => (
  previous.channel === next.channel &&
  previous.statisticsWindow === next.statisticsWindow &&
  previous.viewMode === next.viewMode &&
  previous.refreshLabel === next.refreshLabel &&
  previous.privacyMode === next.privacyMode &&
  previous.balance === next.balance
));

function DispatchFocusPanel({ score, cost, availability, reason, probeTimeline, usageTimeline }: {
  score: string;
  cost: string;
  availability: string;
  reason: string;
  probeTimeline: Channel['probeTimeline'];
  usageTimeline: Channel['usageTimeline'];
}) {
  return (
    <section className="dispatch-focus-panel" aria-label="调度重点">
      <div className="dispatch-focus-main">
        <span>
          <em>调度分</em>
          <strong>{score}</strong>
        </span>
        <span>
          <em>成本</em>
          <strong>{cost}</strong>
        </span>
        <span>
          <em>可用性</em>
          <strong>{availability}</strong>
        </span>
      </div>
      <p title={reason}>{reason}</p>
      <div className="compact-timelines" aria-label="近况概览">
        <CompactTimelineStrip label="探测" points={probeTimeline} />
        <CompactTimelineStrip label="真实" points={usageTimeline} />
      </div>
    </section>
  );
}

function CompactStat({ label, value }: { label: string; value: string }) {
  return (
    <span>
      <b>{value}</b>
      <small>{label}</small>
    </span>
  );
}

function ChannelCardControls({ channel, displayName, privacyMode, onForce, onSaveConfig }: {
  channel: Channel;
  displayName: string;
  privacyMode: boolean;
  onForce: (channel: Channel) => void;
  onSaveConfig: (channel: Channel, config: ChannelConfigDraft) => void;
}) {
  const [cost, setCost] = useState(String(channel.costCnyPerUsd));
  const [priority, setPriority] = useState(String(channel.manualPriority));
  const [probeModel, setProbeModel] = useState(channel.probeModel);
  const [probePrompt, setProbePrompt] = useState(channel.probePrompt);
  const [probeAsClaudeCode, setProbeAsClaudeCode] = useState(channel.probeAsClaudeCode);
  const availableModels = Array.isArray(channel.availableModels)
    ? channel.availableModels.filter((model) => model.trim().length > 0)
    : [];
  const defaultModels = defaultProbeModels(channel.provider);
  const baseProbeModelOptions = availableModels.length > 0 ? availableModels : defaultModels;
  const probeModelOptions = baseProbeModelOptions.includes(probeModel) || probeModel.trim() === ''
    ? baseProbeModelOptions
    : [probeModel, ...baseProbeModelOptions];
  const selectedProbeModel = probeModelOptions.length > 0
    ? (probeModelOptions.includes(probeModel) ? probeModel : probeModelOptions[0] ?? '')
    : probeModel;

  useEffect(() => {
    setCost(String(channel.costCnyPerUsd));
    setPriority(String(channel.manualPriority));
    setProbeModel(channel.probeModel);
    setProbePrompt(channel.probePrompt);
    setProbeAsClaudeCode(channel.probeAsClaudeCode);
  }, [channel.costCnyPerUsd, channel.manualPriority, channel.probeModel, channel.probePrompt, channel.probeAsClaudeCode]);

  return (
    <div className="card-controls">
      <label className="field">
        <span>成本价</span>
        <input
          aria-label={`${displayName} 成本价`}
          type={privacyMode ? 'text' : 'number'}
          value={privacyMode ? PRIVACY_MASK : cost}
          step="0.0001"
          min="0"
          readOnly={privacyMode}
          disabled={privacyMode}
          title={privacyMode ? '隐私模式已隐藏成本价，关闭后可编辑' : undefined}
          onChange={(event) => setCost(event.target.value)}
        />
      </label>
      <label className="field">
        <span>手动优先级</span>
        <input aria-label={`${displayName} 手动优先级`} type="number" value={priority} min="0" max="200" onChange={(event) => setPriority(event.target.value)} />
      </label>
      <label className="field probe-model-field">
        <span>探测模型</span>
        {probeModelOptions.length > 0 ? (
          <select
            aria-label={`${displayName} 探测模型`}
            value={selectedProbeModel}
            onChange={(event) => setProbeModel(event.target.value)}
          >
            {probeModelOptions.map((model) => (
              <option value={model} key={model}>{model}</option>
            ))}
          </select>
        ) : (
          <input
            aria-label={`${displayName} 探测模型`}
            type="text"
            value={probeModel}
            spellCheck={false}
            onChange={(event) => setProbeModel(event.target.value)}
          />
        )}
      </label>
      <button className={channel.forced ? 'icon-button active' : 'icon-button'} type="button" aria-label="强制账号" title="强制账号" onClick={() => onForce(channel)}>
        <Pin size={17} />
      </button>
      <button
        className="icon-button"
        type="button"
        aria-label="保存渠道配置"
        title="保存渠道配置"
        onClick={() => onSaveConfig(channel, {
          costCnyPerUsd: Number(cost) || 0,
          manualPriority: Number(priority) || 0,
          probeModel: selectedProbeModel.trim(),
          probePrompt: probePrompt.trim(),
          probeAsClaudeCode,
        })}
      >
        <Save size={17} />
      </button>
      <label className="field probe-prompt-field">
        <span>探测提示词</span>
        <input
          aria-label={`${displayName} 探测提示词`}
          type="text"
          value={probePrompt}
          maxLength={256}
          placeholder={selectedProbeModel.trim().toLowerCase().startsWith('gpt-image-') ? '默认：生成简单测试图片' : '默认：ping'}
          spellCheck={false}
          onChange={(event) => setProbePrompt(event.target.value)}
        />
      </label>
      {channel.provider === 'anthropic' && (
        <label className={probeAsClaudeCode ? 'probe-mode-switch enabled' : 'probe-mode-switch'}>
          <span>Claude Code 兼容</span>
          <input
            aria-label={`${displayName} Claude Code 兼容探测`}
            type="checkbox"
            role="switch"
            checked={probeAsClaudeCode}
            onChange={(event) => setProbeAsClaudeCode(event.target.checked)}
          />
        </label>
      )}
    </div>
  );
}

function CompactTimelineStrip({ label, points }: { label: string; points: Channel['usageTimeline'] }) {
  const recentPoints = points.slice(-60);
  const latestPoint = recentPoints[recentPoints.length - 1];
  const slots = [
    ...Array<Channel['usageTimeline'][number] | null>(Math.max(0, 60 - recentPoints.length)).fill(null),
    ...recentPoints,
  ];
  return (
    <div className="compact-timeline" aria-label={`${label}请求近 60 次`}>
      <span className={`compact-timeline-label compact-timeline-label-${label === '探测' ? 'probe' : 'usage'}`}>{label}</span>
      <div>
        {slots.map((point, index) => (
          <i
            className={point ? compactPointStatusClass(point) : 'status-empty'}
            title={point ? compactPointTitle(point) : '无记录'}
            key={point ? `${point.checkedAt}-${index}` : `empty-${index}`}
          />
        ))}
      </div>
      <time className="compact-timeline-time" dateTime={latestPoint?.checkedAt} aria-label={label === '探测' ? '最后一次探测时间' : '最后一次真实请求时间'}>
        {latestPoint ? formatCompactTimelineTimestamp(latestPoint.checkedAt) : '--'}
      </time>
    </div>
  );
}

function formatCompactTimelineTimestamp(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '--';
  const pad = (part: number) => String(part).padStart(2, '0');
  return `${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function compactPointStatusClass(point: Channel['usageTimeline'][number]): string {
  if (point.status === 'failed' && point.ttftMs === null && point.pingMs === null) {
    return 'status-uncollected';
  }
  return `status-${point.status}`;
}

function compactPointTitle(point: Channel['usageTimeline'][number]): string {
  const time = new Date(point.checkedAt);
  const timeText = Number.isNaN(time.getTime())
    ? '未知时间'
    : time.toLocaleTimeString('zh-CN', { timeZone: 'Asia/Shanghai', hour: '2-digit', minute: '2-digit', hour12: false });
  const statusText = point.status === 'healthy'
    ? '正常'
    : point.status === 'degraded'
      ? '降级'
      : point.status === 'failed'
        ? '错误'
        : point.status === 'cooldown'
          ? '冷却'
          : '空闲';
  return `${timeText} · ${statusText}`;
}

function defaultProbeModels(provider: Channel['provider']): string[] {
  if (provider === 'anthropic') {
    return [
      'claude-sonnet-4-6',
      'claude-haiku-4-5-20251001',
      'claude-sonnet-4-5-20250929',
      'claude-opus-4-5-20251101',
      'claude-3-5-haiku-20241022',
    ];
  }
  if (provider === 'openai') {
    return ['gpt-5.4-mini', 'gpt-5.4', 'gpt-5.5'];
  }
  if (provider === 'gemini') {
    return ['gemini-2.0-flash', 'gemini-2.5-flash', 'gemini-2.5-pro'];
  }
  if (provider === 'openrouter') {
    return ['gpt-5.4-mini', 'gpt-4o-mini'];
  }
  return [];
}

function formatScore(value?: number): string {
  if (!Number.isFinite(value ?? NaN)) return '-';
  return Math.round(value ?? 0).toLocaleString('zh-CN');
}

function formatCompactLatency(value: number | null): string {
  if (value === null || !Number.isFinite(value)) return '-';
  if (value >= 1000) return `${(value / 1000).toFixed(2)}s`;
  return `${Math.round(value)}ms`;
}

function formatBalance(value: number, unit: string): string {
  if (!Number.isFinite(value)) return '-';
  return `${value.toFixed(2)} ${unit || 'USD'}`;
}
